'use client'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useEmailVerification } from '@/hooks/useEmailVerification'
import { cn } from '@/lib/utils'
import { AlertCircle, Mail, RefreshCw, X } from 'lucide-react'
import { useState } from 'react'

export const EmailVerificationBanner = () => {
  const {
    needsVerification,
    isResending,
    resendVerificationEmail,
    refreshVerificationStatus,
    isInitialCheckComplete,
    canResend,
    cooldownRemaining,
  } = useEmailVerification()
  const { userData } = useAuth()

  const [isDismissed, setIsDismissed] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Don't show banner for users created via invite
  const createdViaInvite = userData?.createdViaInvite || false

  if (!isInitialCheckComplete || !needsVerification || isDismissed || createdViaInvite) {
    return null
  }

  const handleResend = async () => {
    const success = await resendVerificationEmail()
    if (success) {
      setIsDismissed(true)
    }
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await refreshVerificationStatus()
      setTimeout(() => {
        setIsRefreshing(false)
      }, 500)
    } catch (error) {
      setIsRefreshing(false)
    }
  }

  return (
    <div className='bg-yellow-50 border-b border-yellow-200 px-3 sm:px-4 py-2 sm:py-3'>
      <div className='max-w-7xl mx-auto'>
        {/* Desktop layout */}
        <div className='hidden sm:flex items-center justify-between'>
          <div className='flex items-center space-x-3'>
            <AlertCircle className='h-5 w-5 text-yellow-600 shrink-0' />
            <div className='flex-1'>
              <p className='text-sm text-yellow-800'>
                <span className='font-medium'>Email verification required:</span> Please verify your email address to
                access all features.
              </p>
            </div>
          </div>

          <div className='flex items-center space-x-3'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={handleRefresh}
              disabled={isRefreshing}
              className='text-yellow-800 border-yellow-300 hover:bg-yellow-100'
            >
              <RefreshCw className={cn(`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`)} />
              {isRefreshing ? 'Checking...' : 'Check Status'}
            </Button>

            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={handleResend}
              disabled={isResending || !canResend}
              className='text-yellow-800 border-yellow-300 hover:bg-yellow-100'
            >
              <Mail className='h-4 w-4 mr-2' />
              {isResending ? 'Sending...' : !canResend ? `Resend in ${cooldownRemaining}s` : 'Resend Email'}
            </Button>

            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={() => setIsDismissed(true)}
              className='text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100'
            >
              <X className='h-4 w-4' />
            </Button>
          </div>
        </div>

        {/* Mobile layout */}
        <div className='sm:hidden'>
          <div className='flex items-start space-x-2 mb-2'>
            <AlertCircle className='h-4 w-4 text-yellow-600 shrink-0 mt-0.5' />
            <div className='flex-1 min-w-0'>
              <p className='text-xs text-yellow-800 leading-relaxed'>
                <span className='font-medium'>Email verification required:</span> Please verify your email address to
                access all features.
              </p>
            </div>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setIsDismissed(true)}
              className='text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100 p-1 h-auto'
            >
              <X className='h-3 w-3' />
            </Button>
          </div>

          <div className='flex items-center space-x-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={handleRefresh}
              disabled={isRefreshing}
              className='text-yellow-800 border-yellow-300 hover:bg-yellow-100 text-xs px-2 py-1 h-7'
            >
              <RefreshCw className={cn(`h-3 w-3 mr-1 ${isRefreshing ? 'animate-spin' : ''}`)} />
              {isRefreshing ? 'Checking...' : 'Check'}
            </Button>

            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={handleResend}
              disabled={isResending || !canResend}
              className='text-yellow-800 border-yellow-300 hover:bg-yellow-100 text-xs px-2 py-1 h-7'
            >
              <Mail className='h-3 w-3 mr-1' />
              {isResending ? 'Sending...' : !canResend ? `Resend in ${cooldownRemaining}s` : 'Resend'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
